<template>
  <layout title="Live session">
    <transition name="slide-fade">
      <div
        class="classroom-session-sidebar shadow p-3 pt-4 d-flex flex-column"
        v-if="can_accept_requests && showRequests"
      >
        <button
          type="button"
          class="close"
          data-dismiss="alert"
          @click="showRequests = false"
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </button>
        <h5>Pending Join Requests</h5>
        <hr />
        <div class="alert alert-info" v-if="!pending_session_requests.length">
          No requests
        </div>
        <div
          class="mt-2"
          v-for="req in pending_session_requests"
          :key="req.user.id"
        >
          {{ req.user.first_name + " " + req.user.last_name }}

          <div class="float-right">
            <button
              class="btn btn-xs btn btn-outline-success btn-light"
              @click="acceptRejectRequest(req.user.id, true)"
            >
              Accept
            </button>
            <button
              class="ml-2 btn btn-xs btn btn-outline-danger btn-light"
              @click="acceptRejectRequest(req.user.id, false)"
            >
              Reject
            </button>
          </div>
        </div>

        <div v-if="rejected_session_requests.length > 0">
          <h5 class="mt-5">Rejected Join Requests</h5>
          <hr />
          <div
            class="mt-2"
            v-for="req in rejected_session_requests"
            :key="req.user.id"
          >
            {{ req.user.first_name + " " + req.user.last_name }}
            <div class="float-right">
              <button
                class="btn btn-xs btn btn-outline-success btn-light"
                @click="acceptRejectRequest(req.user.id, true)"
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="slide-fade">
      <div
        class="classroom-session-sidebar shadow p-3 pt-4 d-flex flex-column"
        v-if="showChat"
      >
        <button
          type="button"
          class="close"
          data-dismiss="alert"
          @click="showChat = false"
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </button>

        <ul
          class="nav nav-pills mb-2 pb-2 border-bottom ml-n3 mr-n3 px-3"
          id="pills-tab"
          role="tablist"
        >
          <li class="nav-item">
            <a
              class="nav-link active"
              id="pills-chat-tab"
              data-toggle="pill"
              href="#pills-chat"
              role="tab"
              aria-controls="pills-chat"
              aria-selected="true"
            >
              Chat
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link"
              id="pills-people-tab"
              data-toggle="pill"
              href="#pills-people"
              role="tab"
              aria-controls="pills-people"
              aria-selected="false"
            >
              People ({{ sessionUsers.length }})
            </a>
          </li>
        </ul>

        <div class="tab-content">
          <div
            class="tab-pane fade show active chat-container"
            id="pills-chat"
            role="tabpanel"
            aria-labelledby="pills-home-tab"
          >
            <div class="chat-message-container d-flex flex-column">
              <div class="mb-auto chat-messages">
                <div
                  class="chat-message-single mb-3 row"
                  v-for="message in chat_messages.data"
                  :key="message.id"
                  :class="message.pending ? 'pending' : ''"
                >
                  <div class="col-md-2">
                    <img
                      class="img-fluid mr-3"
                      :src="`https://ui-avatars.com/api/?rounded=true&name=${message.user.name}&length=1&size=70&bold=true`"
                      width="35"
                    />
                  </div>
                  <div class="col-md-10 pl-0">
                    <div class="username">
                      {{ message.user.name }}
                      <span class="datetime">{{ message.created_at }}</span>
                    </div>
                    <div
                      class="message"
                      :class="{
                        current: $page.props.authUser.id == message.user.id,
                      }"
                    >
                      {{ message.message }}
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <textarea
                  v-model="chatMessage"
                  @keyup.@="tagUsers()"
                  @keydown.enter.exact.prevent="sendMessage()"
                  rows="2"
                  class="form-control mt-2"
                />

                <small id="emailHelp" class="form-text text-muted">
                  Press Enter to send
                </small>
              </div>
            </div>
          </div>
          <div
            class="tab-pane fade"
            id="pills-people"
            role="tabpanel"
            aria-labelledby="pills-people-tab"
          >
            <div
              v-for="user in sessionUsers"
              :key="user.id"
              class="w-100 mt-2 mb-2"
            >
              <img
                class="img-fluid mr-3"
                :src="`https://ui-avatars.com/api/?rounded=true&name=${user.name}&length=1&size=70&bold=true`"
                width="35"
              />
              {{ user.name }}
            </div>
          </div>
        </div>
      </div>
    </transition>

    <div class="row">
      <div class="col-md-12">
        <div
          class="card shadow-sm p-3 mb-5 bg-white rounded mt-2"
          style="min-height: 75vh"
        >
          <div class="card-body col-md-12">
            <div class="row">
              <div class="col-md-6">
                <h2 class="page-heading">
                  {{ classroom.data.name }}
                  <span style="font-size: 13px">
                    [ {{ session.start_datetime }} -
                    {{ session.end_datetime }} ]
                  </span>
                </h2>
              </div>
              <div class="col-md-6">
                <inertia-link
                  class="btn btn-primary float-right"
                  :href="
                    route(
                      'classrooms.assigned.view.sessions',
                      classroom.data.id
                    )
                  "
                >
                  Back to classroom
                </inertia-link>

                <button
                  v-if="can_accept_requests"
                  class="btn btn-light float-right mr-3"
                  :class="{ attention: pending_session_requests.length }"
                  @click="showRequests = !showRequests"
                >
                  <img
                    style="width: 20px"
                    src="/images/add.svg"
                    title="Join Requests"
                  />
                  <span class="badge badge-dark ml-1">
                    {{ pending_session_requests.length }}
                  </span>
                </button>

                <button
                  class="btn btn-light float-right mr-3"
                  @click="toggleChatBox()"
                >
                  <span v-if="hasUnreadChat" class="chat-indicator">
                    &bull;
                  </span>
                  <img
                    style="width: 20px"
                    src="/images/chat.svg"
                    title="Chat"
                  />
                  Chat ({{ sessionUsers.length }})
                </button>
              </div>
            </div>
            <hr />

            <div class="row mt-3">
              <div
                class="col-md-12"
                v-if="session.metadata && session.metadata.live"
              >
                <div v-if="liveVideoSessionEnded" class="not-connected-alert">
                  <h3>You are not connected to the live session.</h3>
                  <button class="btn btn-success mt-4" @click="startVideo()">
                    Join Session
                  </button>
                </div>

                <div v-else id="meet" @contextmenu.prevent="false"></div>
              </div>
              <div class="col-md-12 slider" v-else>
                <ContentSlider :files="content.main" />
              </div>
            </div>

            <div v-if="content.files.length > 0">
              <div class="row mt-5">
                <div class="col-md-12">
                  <h4>Other Material</h4>
                  <hr />
                </div>
              </div>

              <div class="row mt-3" v-if="!content.files.length">
                <div class="offset-md-2 col-md-8 alert alert-info">
                  No content
                </div>
              </div>

              <div class="row mt-3">
                <div
                  v-for="other_file in content.files"
                  :key="other_file.id"
                  class="col-md-2 p-1"
                >
                  <div class="card h-100">
                    <span class="size-label">
                      {{ formatBytes(other_file.meta_data.size, 0) }}
                    </span>
                    <img
                      class="card-img-top"
                      v-if="other_file.file_type == 'image/jpeg'"
                      :src="
                        getMediaItemLink(
                          other_file.file_path,
                          other_file.storage_disk
                        )
                      "
                      alt="Card image cap"
                    />
                    <img
                      class="card-img-top"
                      v-else
                      src="/images/files.png"
                      alt="Card image cap"
                    />

                    <div class="file-box card-body" :title="other_file.name">
                      <a
                        :href="
                          getMediaItemLink(
                            other_file.file_path,
                            other_file.storage_disk
                          )
                        "
                        download
                        target="_blank"
                      >
                        {{ other_file.name }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </layout>
</template>


<script>
import AppLayout from "../../Layouts/AppLayout";
import ContentSlider from "../../../Components/ContentSlider";

export default {
  layout: [AppLayout],
  props: {
    classroom: Object,
    session: Object,
    flash: Object,
    content: Object,
    can_accept_requests: Boolean,
    pending_session_requests: Object,
    rejected_session_requests: Object,
    chat_messages: Object,
    csrf_token: String,
    authUser: Object,
    meet_host: String,
    s: String, // token
  },
  components: {
    ContentSlider: ContentSlider,
  },
  mounted() {
    this.requests = this.pending_session_requests;

    // Todo: remove this line. Used as a workaround to access array inside proxy
    const fix = this.chat_messages.data;

    Echo.setCsrf(this.csrf_token);

    // listen to session join requests
    Echo.private(`session-join-request-${this.session.id}`).listen(
      "SessionJoinRequest",
      (e) => {
        this.pending_session_requests.push({
          session_id: e.session.id,
          user_id: e.user.id,
          user: {
            first_name: e.user.first_name,
            last_name: e.user.last_name,
            email: e.user.email,
            id: e.user.id,
          },
        });
      }
    );

    // listen to chat messages
    Echo.private(`classroom-session-${this.session.id}`)
      .listen("ChatMessagePosted", (e) => {
        this.chat_messages.data.push(e);

        if (!this.showChat) {
          this.hasUnreadChat = true;
        }

        this.$nextTick(function () {
          this.scrollChatDiv();
        });
      })
      .listenForWhisper("typing", (e) => {
        console.log("someone is typing ", e.name);
      });

    // listen to session join events
    Echo.join(`classroom-session-${this.session.id}`)
      .here((users) => {
        this.sessionUsers = users;
      })
      .joining((user) => {
        this.sessionUsers.push(user);

        this.$toast.info(`${user.name} joined the session`, {
          position: "top-right",
          pauseOnHover: false,
        });
      })
      .leaving((user) => {
        this.sessionUsers = this.sessionUsers.filter(
          (sessionUser) => sessionUser.id != user.id
        );

        this.$toast.show(`${user.name} left the session`, {
          position: "top-right",
          pauseOnHover: false,
        });
      });

    // load video conference

    if (this.session.metadata && this.session.metadata.live) {
      this.startVideo();
    }

    // logout polling
    window.setInterval(this.checkSession, 10000);
  },

  unmounted() {
    Echo.leave(`classroom-session-${this.session.id}`);
    Echo.leave(`session-join-request-${this.session.id}`);
  },
  data() {
    return {
      showRequests: false,
      showChat: false,
      requests: [],
      sessionUsers: [],
      chatMessage: "",
      hasUnreadChat: false,
      peerConnections: {},
      sessionUsers: [],
      stream: null,
      liveVideoSessionEnded: false,
    };
  },

  watch: {
    showChat: function () {
      this.$nextTick(function () {
        if (this.showChat) {
          this.scrollChatDiv();
        }
      });
    },
  },
  methods: {
    checkSession() {
      $.get(route("auth.session.status"))
        .catch(e => location.reload());
    },
    formatBytes(bytes, decimals = 2) {
      if (bytes === 0) return "0 Bytes";

      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    },
    acceptRejectRequest(userId, accepted) {
      this.$inertia.post(
        route("classrooms.assigned.sessions.request.accept.reject", [
          this.classroom.data.id,
          this.session.id,
        ]),
        {
          user_id: userId,
          accepted: accepted,
        }
      );
    },
    sendMessage() {
      if (this.chatMessage == "") {
        return false;
      }

      this.$inertia.post(
        route("classrooms.assigned.sessions.chat", [
          this.classroom.data.id,
          this.session.id,
        ]),
        {
          message: this.chatMessage,
        },
        {
          onStart: () => {
            this.chatMessage = "";
          },
          onFinish: () => {},
        }
      );
    },
    tagUsers() {
      //
    },
    scrollChatDiv() {
      let chatContainer = $(".chat-messages")[0];
      if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    },
    toggleChatBox() {
      this.hasUnreadChat = false;
      this.showChat = !this.showChat;
    },

    loadJipsiAPI(meetHost) {
      return new Promise((resolve) => {
        const script = document.createElement("script");
        script.src = `https://${meetHost}/external_api.js`;
        script.async = true;
        script.onload = resolve;
        document.body.appendChild(script);
      });
    },
    startVideo() {
      if (this.meet_host && this.meet_host != "") {
        this.liveVideoSessionEnded = false;
        this.loadJipsiAPI(this.meet_host).then(() => {
          
          const domain = this.meet_host;

          const options = {
            roomName: this.session.id,
            width: "100%",
            height: 600,
            parentNode: document.querySelector("#meet"),
            configOverwrite: {
              startWithAudioMuted: true,
              startWithVideoMuted: true,
              requireDisplayName: false,
              prejoinPageEnabled: false,
              etherpad_base: "https://wbo.ophir.dev/boards/",
            },
            interfaceConfigOverwrite: {
              APP_NAME: "JLEA LMS",
              BRAND_WATERMARK_LINK: "",
              DEFAULT_LOGO_URL: "",
              DEFAULT_WELCOME_PAGE_LOGO_URL: "",
              DISPLAY_WELCOME_FOOTER: false,
              HIDE_INVITE_MORE_HEADER: true,
              JITSI_WATERMARK_LINK: "",
              MOBILE_APP_PROMO: false,
              SHOW_JITSI_WATERMARK: false,
              SHOW_PROMOTIONAL_CLOSE_PAGE: false,
              SUPPORT_URL: "",
              TOOLBAR_BUTTONS: [
                "microphone",
                "camera",
                "closedcaptions",
                "desktop",
                "fullscreen",
                "fodeviceselection",
                "hangup",
                "profile",
                "chat",
                "settings",
                "raisehand",
                "videoquality",
                "filmstrip",
                "tileview",
                "mute-everyone",
                // "etherpad"
              ],
              SHOW_JITSI_WATERMARK_FOR_GUESTS: false,
              SHOW_WATERMARK_FOR_GUESTS: false,
              SHOW_PROMOTIONAL_CLOSE_PAGE: false,
            },
            userInfo: {
              email: this.authUser.email,
              displayName: this.authUser.first_name,
            },
            jwt: this.s,
          };

          const api = new JitsiMeetExternalAPI(domain, options);

          this.liveVideoSessionEnded = false;

          api.getIFrame().addEventListener("contextmenu", (e) => {
            e.preventDefault();
            return false;
          });

          api.addEventListener(
            "readyToClose",
            (event) => (this.liveVideoSessionEnded = true)
          );
        });
      }
    },
  },
};
</script>